import { Box, Button, Drawer, Text } from "@mantine/core"
import { useDisclosure, useMediaQuery } from "@mantine/hooks"
import { ListPlusIcon } from "@phosphor-icons/react"
import React from "react"
import { useTranslation } from "react-i18next"
import { HelpIcon } from "@dehub/theme"
import type { PriceBreakdown } from "../../../apis/pricing"
import type { CartItem } from "../../../store/Cart"
import { usePermission } from "../../permission"
import { ListState } from "../../ui"
import { SaveListModal } from "../saved-lists/SaveListModal"
import { useCartPreviews, useSupplierNetworks } from "./cart-model"
import { CartCheckout } from "./CartDrawerCheckout"
import { CartDrawerItems } from "./CartDrawerItems"
import classes from "./CartDrawer.module.css"
import type { SupplierCartGroup } from "./types"

type Props = {
  opened: boolean
  onClose: () => void
  cart: CartItem[]
  cartBySupplier: Record<string, SupplierCartGroup>
  totalItems: number
  totalAmount: number
  /** Cart lines priced at their cart quantity, keyed `${supplierId}:${variantId}` (catalogue page). */
  breakdowns: Record<string, PriceBreakdown>
  onUpdateQuantity: (productId: string, supplierBusinessId: string, qty: number) => void
  onRemoveFromCart: (productId: string, supplierBusinessId: string) => void
}

/**
 * Cart drawer (canvas OrdB_Cart, B23): 720 wide, opened under the app header.
 * One step: the lines grouped by supplier, then the checkout form (receiver,
 * delivery, payment term, note, totals) in the same scrolling column, with
 * «Ноорог хадгалах» / «Захиалга илгээх» pinned in the foot.
 */
export function CartDrawer({
  opened,
  onClose,
  cart,
  cartBySupplier,
  totalItems,
  breakdowns,
  onUpdateQuantity,
  onRemoveFromCart,
}: Props) {
  const { t } = useTranslation(["order", "common"])
  const isMobile = useMediaQuery("(max-width: 62em)")
  const ORD_SAVE = usePermission("ORD_SAVE")
  const ORD_SEND = usePermission("ORD_SEND")

  const supplierIds = Object.keys(cartBySupplier)
  const { networks } = useSupplierNetworks(opened ? supplierIds : [])
  const [saveListOpened, { open: openSaveList, close: closeSaveList }] = useDisclosure(false)

  // D3: gift lines shown under each supplier's lines.
  const previews = useCartPreviews(cartBySupplier, opened && (ORD_SAVE.hasAccess || ORD_SEND.hasAccess))

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={isMobile ? "100%" : 720}
      padding={0}
      title={
        <Box>
          <Text className={classes.drawerTitle}>
            {t("order:cart.title")}
            <HelpIcon helpKey="order.buyer-sales-catalog.section.cart" variant="section" />
          </Text>
          <Text className={classes.sub}>{t("order:cart.headSub", { types: cart.length, count: totalItems })}</Text>
        </Box>
      }
      closeButtonProps={{ "aria-label": t("order:cart.close"), size: "lg" }}
      overlayProps={{ backgroundOpacity: 0.35 }}
      classNames={{
        overlay: classes.drawerOverlay,
        inner: classes.drawerInner,
        content: classes.drawerContent,
        header: classes.drawerHeader,
        body: classes.drawerBody,
      }}
    >
      {cart.length === 0 ? (
        <Box className={classes.drawerScroll}>
          <ListState
            state="empty"
            title={t("order:cart.emptyTitle")}
            description={t("order:cart.emptyText")}
            action={{ label: t("order:cart.emptyAction"), onClick: onClose }}
          />
        </Box>
      ) : (
        // Mounted only while open, so every opening starts from the network defaults.
        opened && (
          <CartCheckout
            items={cart}
            compact
            onSubmitted={onClose}
            header={
              <CartDrawerItems
                cartBySupplier={cartBySupplier}
                networks={networks}
                breakdowns={breakdowns}
                previews={previews}
                onUpdateQuantity={onUpdateQuantity}
                onRemoveFromCart={onRemoveFromCart}
              />
            }
            footerExtra={
              // B25: the cart's lines of one supplier become a saved list.
              <Button variant="subtle" color="gray" size="sm" leftSection={<ListPlusIcon size={18} />} onClick={openSaveList}>
                {t("bulkOrder:saved.saveAsList")}
              </Button>
            }
          />
        )
      )}
      <SaveListModal
        opened={saveListOpened}
        onClose={closeSaveList}
        groups={supplierIds.map((id) => ({
          supplierBusinessId: id,
          supplierName: cartBySupplier[id]?.supplierName || "",
          lines: cartBySupplier[id].items.map((i) => ({ variantId: i.variantId, parentVariantId: i.variantParentId, quantity: i.quantity })),
        }))}
      />
    </Drawer>
  )
}
